/**
 * `localStorage` that cannot throw. Safari in private mode, a hardened corporate profile and
 * "block third-party cookies" all expose a `localStorage` object whose `setItem` throws, so a
 * bare `createJSONStorage(() => localStorage)` takes the whole app down on the first write.
 * Falling back to an in-memory map keeps the store working for the session and simply forgets
 * across reloads — the correct degradation for preferences.
 */
export function createSafeStorage(): Storage {
  try {
    const candidate = globalThis.localStorage as Storage | undefined;
    if (candidate && typeof candidate.setItem === 'function') {
      const probe = '__cenorion_probe__';
      candidate.setItem(probe, probe);
      candidate.removeItem(probe);
      return candidate;
    }
  } catch {
    // Storage present but unusable — fall through to the in-memory shim.
  }

  const memory = new Map<string, string>();
  return {
    get length() {
      return memory.size;
    },
    clear: () => memory.clear(),
    getItem: (key) => memory.get(key) ?? null,
    key: (index) => [...memory.keys()][index] ?? null,
    removeItem: (key) => void memory.delete(key),
    setItem: (key, value) => void memory.set(key, value),
  };
}
